import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2, MessageSquare, FileText, Users, Mail } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { getNotificationSettings, updateNotificationSettings } from '../services/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from '../hooks/use-toast';
import { InstallGuide } from './InstallGuide';

const NotificationSettings = () => {
  const [guideOpen, setGuideOpen] = useState(false);
  const { 
    permission, 
    isEnabled, 
    loading: notifLoading, 
    supported,
    enableNotifications, 
    disableNotifications 
  } = useNotifications();
  const { currentUser, isOwner } = useAuth();
  
  const [settings, setSettings] = useState({
    newPosts: true,
    comments: true,
    chatReplies: true,
    directMessages: true,
    newMembers: true,
    emailNewPosts: true,
    emailComments: true,
    emailDirectMessages: true,
    emailNewMembers: true,
    emailAnnouncements: true
  });
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getNotificationSettings();
        setSettings(data);
      } catch (error) {
      } finally {
        setLoadingSettings(false);
      }
    };

    if (currentUser) {
      loadSettings();
    }
  }, [currentUser]);

  const handleToggle = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    
    setSavingSettings(true);
    try {
      await updateNotificationSettings({ [key]: value });
    } catch (error) {
      // Revert on error
      setSettings(settings);
      toast({
        title: 'Error',
        description: 'Failed to save notification settings',
        variant: 'destructive'
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleEnableNotifications = async () => {
    const success = await enableNotifications();
    if (success) {
      // Reload settings after enabling
      try {
        const data = await getNotificationSettings();
        setSettings(data);
      } catch (error) {
      }
    }
  };

  if (!supported) {
    return (
      <Card className="bg-gray-900/90 border-2 border-amber-900/30">
        <CardHeader>
          <CardTitle className="font-headline text-amber-500 tracking-wider flex items-center gap-2">
            <BellOff className="w-5 h-5" />
            NOTIFICATIONS
          </CardTitle>
          <CardDescription className="font-typewriter text-gray-400">
            Push notifications are not supported in this browser
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900/90 border-2 border-amber-900/30">
      <CardHeader>
        <CardTitle className="font-headline text-amber-500 tracking-wider flex items-center gap-2">
          <Bell className="w-5 h-5" />
          PUSH NOTIFICATIONS
        </CardTitle>
        <CardDescription className="font-typewriter text-gray-400">
          Stay updated with instant alerts on your devices
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Master Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-amber-900/20">
          <div className="flex items-center gap-3">
            {isEnabled ? (
              <Bell className="w-6 h-6 text-green-500" />
            ) : (
              <BellOff className="w-6 h-6 text-gray-500" />
            )}
            <div>
              <p className="font-headline text-white tracking-wide">
                {isEnabled ? 'NOTIFICATIONS ENABLED' : 'NOTIFICATIONS DISABLED'}
              </p>
              <p className="font-typewriter text-sm text-gray-400">
                {isEnabled 
                  ? 'You will receive push notifications' 
                  : permission === 'denied' 
                    ? 'Enable in browser settings' 
                    : 'Click to enable push notifications'}
              </p>
            </div>
          </div>
          <Button
            onClick={isEnabled ? disableNotifications : handleEnableNotifications}
            disabled={notifLoading || permission === 'denied'}
            variant={isEnabled ? 'outline' : 'default'}
            className={isEnabled 
              ? 'border-red-600 text-red-500 hover:bg-red-600/10' 
              : 'bg-amber-600 hover:bg-amber-700 text-black'}
            data-testid="toggle-notifications-btn"
          >
            {notifLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isEnabled ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </Button>
        </div>
        {!isEnabled && (
          <button type="button" onClick={() => setGuideOpen(true)} className="text-xs font-typewriter text-amber-500 hover:text-amber-300 underline underline-offset-2" data-testid="ios-install-guide-link">
            On iPhone? See how to enable alerts (3 steps)
          </button>
        )}
        <InstallGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

        {/* Individual Notification Settings */}
        {isEnabled && !loadingSettings && (
          <div className="space-y-4">
            <h4 className="font-headline text-sm text-amber-600 tracking-wider">
              NOTIFICATION PREFERENCES
            </h4>
            
            {/* New Posts */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  New Case Files
                </Label>
              </div>
              <Switch
                checked={settings.newPosts}
                onCheckedChange={(v) => handleToggle('newPosts', v)}
                disabled={savingSettings}
              />
            </div>

            {/* Comments */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Comments on Posts
                </Label>
              </div>
              <Switch
                checked={settings.comments}
                onCheckedChange={(v) => handleToggle('comments', v)}
                disabled={savingSettings}
              />
            </div>

            {/* Direct Messages */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Direct Messages
                </Label>
              </div>
              <Switch
                checked={settings.directMessages}
                onCheckedChange={(v) => handleToggle('directMessages', v)}
                disabled={savingSettings}
              />
            </div>

            {/* Chat Replies */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Chat Replies
                </Label>
              </div>
              <Switch
                checked={settings.chatReplies}
                onCheckedChange={(v) => handleToggle('chatReplies', v)}
                disabled={savingSettings}
              />
            </div>

            {/* New Members (Owner only) */}
            {isOwner && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-amber-500" />
                  <Label className="font-typewriter text-gray-300 cursor-pointer">
                    New Investigators Join
                  </Label>
                </div>
                <Switch
                  checked={settings.newMembers}
                  onCheckedChange={(v) => handleToggle('newMembers', v)}
                  disabled={savingSettings}
                />
              </div>
            )}
          </div>
        )}

        {/* Email Notification Settings */}
        {!loadingSettings && (
          <div className="space-y-4 pt-2">
            <h4 className="font-headline text-sm text-amber-600 tracking-wider flex items-center gap-2">
              <Mail className="w-4 h-4" />
              EMAIL NOTIFICATIONS
            </h4>
            <p className="font-typewriter text-xs text-gray-500">
              Receive email alerts — works independently of push notifications
            </p>

            {/* Email: Announcements */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Bureau Announcements
                </Label>
              </div>
              <Switch
                checked={settings.emailAnnouncements !== false}
                onCheckedChange={(v) => handleToggle('emailAnnouncements', v)}
                disabled={savingSettings}
                data-testid="email-announcements-toggle"
              />
            </div>

            {/* Email: New Posts */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  New Case Files
                </Label>
              </div>
              <Switch
                checked={settings.emailNewPosts}
                onCheckedChange={(v) => handleToggle('emailNewPosts', v)}
                disabled={savingSettings}
                data-testid="email-new-posts-toggle"
              />
            </div>

            {/* Email: Comments */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Comments on Posts
                </Label>
              </div>
              <Switch
                checked={settings.emailComments}
                onCheckedChange={(v) => handleToggle('emailComments', v)}
                disabled={savingSettings}
                data-testid="email-comments-toggle"
              />
            </div>

            {/* Email: Direct Messages */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-amber-500" />
                <Label className="font-typewriter text-gray-300 cursor-pointer">
                  Direct Messages
                </Label>
              </div>
              <Switch
                checked={settings.emailDirectMessages}
                onCheckedChange={(v) => handleToggle('emailDirectMessages', v)}
                disabled={savingSettings}
                data-testid="email-dm-toggle"
              />
            </div>

            {/* Email: New Members (Owner only) */}
            {isOwner && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-amber-500" />
                  <Label className="font-typewriter text-gray-300 cursor-pointer">
                    New Investigators Join
                  </Label>
                </div>
                <Switch
                  checked={settings.emailNewMembers}
                  onCheckedChange={(v) => handleToggle('emailNewMembers', v)}
                  disabled={savingSettings}
                  data-testid="email-new-members-toggle"
                />
              </div>
            )}
          </div>
        )}

        {loadingSettings && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationSettings;
